<script>
  import { router } from '@inertiajs/svelte'
  import { onMount } from 'svelte';

  // Props: address and errors
  // Use $props rune to handle props reactivity
  let { address = null, errors = {} } = $props();
  
  // Field labels for error messages
  const fieldLabels = {
    'address.line1': 'Street address',
    'address.city': 'City',
    'address.country': 'Country'
  };

  // Function to get error message with field label
  function getErrorMessage(errorKey) {
    const label = fieldLabels[errorKey] || errorKey.split('.').pop();
    return `${label} ${errors[errorKey]}`;
  }

  // Address form state
  let addressForm = $state({
    id: null,
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US'
  });

  // Initialize form with existing address data if available
  onMount(() => {
    console.log('AddressSelector mounted');
    if (address) {
      console.log('Initial address:', address);
      addressForm = {
        id: address.id,
        line1: address.line1 || '',
        line2: address.line2 || '',
        city: address.city || '',
        state: address.state || '',
        postal_code: address.postal_code || '',
        country: address.country || 'US'
      };
    }
  });
  
  // Effect to monitor address changes and update form
  $effect(() => {
    if (address) {
      console.log('Address updated:', address);
      addressForm = {
        id: address.id,
        line1: address.line1 || '',
        line2: address.line2 || '',
        city: address.city || '',
        state: address.state || '',
        postal_code: address.postal_code || '',
        country: address.country || 'US'
      };
    }
  });

  // Function to get the current address data for the parent component
  export function getAddressData() {
    if (addressForm.id) {
      // Return ID and updated fields if editing an existing address
      return {
        address_attributes: {
          id: addressForm.id,
          line1: addressForm.line1,
          line2: addressForm.line2,
          city: addressForm.city,
          state: addressForm.state,
          postal_code: addressForm.postal_code,
          country: addressForm.country
        }
      };
    } else {
      // Return fields for a new address
      return {
        address_attributes: {
          line1: addressForm.line1,
          line2: addressForm.line2,
          city: addressForm.city,
          state: addressForm.state,
          postal_code: addressForm.postal_code,
          country: addressForm.country
        }
      };
    }
  }
</script>

<div class="mb-6 mt-8 border-t pt-6">
  <h2 class="text-lg font-semibold mb-4">Property Address</h2>
  
  <!-- Address Form -->
  <div class="border border-gray-200 rounded-md p-4 bg-gray-50 mb-4">
    <div class="mb-4">
      <label for="line1" class="block text-gray-700 font-medium mb-2">Street Address*</label>
      <input 
        type="text" 
        id="line1" 
        bind:value={addressForm.line1} 
        class="w-full border-gray-300 rounded-md shadow-sm px-4 py-2 focus:ring-brand-600 focus:border-brand-600"
        placeholder="e.g. 123 Main St"
      />
      {#if errors['address.line1']}
        <p class="text-red-500 text-sm mt-1">{getErrorMessage('address.line1')}</p>
      {/if}
    </div>
    
    <div class="mb-4">
      <label for="line2" class="block text-gray-700 font-medium mb-2">Unit/Suite/Apt</label>
      <input 
        type="text" 
        id="line2" 
        bind:value={addressForm.line2} 
        class="w-full border-gray-300 rounded-md shadow-sm px-4 py-2 focus:ring-brand-600 focus:border-brand-600"
        placeholder="e.g. Apt 4B"
      />
    </div>
    
    <div class="grid grid-cols-2 gap-4 mb-4">
      <div>
        <label for="city" class="block text-gray-700 font-medium mb-2">City*</label>
        <input 
          type="text" 
          id="city" 
          bind:value={addressForm.city} 
          class="w-full border-gray-300 rounded-md shadow-sm px-4 py-2 focus:ring-brand-600 focus:border-brand-600"
        />
        {#if errors['address.city']}
          <p class="text-red-500 text-sm mt-1">{getErrorMessage('address.city')}</p>
        {/if}
      </div>
      
      <div>
        <label for="state" class="block text-gray-700 font-medium mb-2">State/Province</label>
        <input 
          type="text" 
          id="state" 
          bind:value={addressForm.state} 
          class="w-full border-gray-300 rounded-md shadow-sm px-4 py-2 focus:ring-brand-600 focus:border-brand-600"
        />
      </div>
    </div>
    
    <div class="grid grid-cols-2 gap-4 mb-4">
      <div>
        <label for="postal_code" class="block text-gray-700 font-medium mb-2">Postal Code</label>
        <input 
          type="text" 
          id="postal_code" 
          bind:value={addressForm.postal_code} 
          class="w-full border-gray-300 rounded-md shadow-sm px-4 py-2 focus:ring-brand-600 focus:border-brand-600"
        />
      </div>
      
      <div>
        <label for="country" class="block text-gray-700 font-medium mb-2">Country*</label>
        <input 
          type="text" 
          id="country" 
          bind:value={addressForm.country} 
          class="w-full border-gray-300 rounded-md shadow-sm px-4 py-2 focus:ring-brand-600 focus:border-brand-600"
        />
        {#if errors['address.country']}
          <p class="text-red-500 text-sm mt-1">{getErrorMessage('address.country')}</p>
        {/if}
      </div>
    </div>
  </div>
</div>